'use client';

import { Download, Loader2, Maximize, Minimize, MoreVertical } from 'lucide-react';

import React, { useCallback, useMemo, useState } from 'react';

import { SimpleDropdown } from '@/shared/components';
import type { DropdownOption } from '@/shared/components/SimpleDropdown';

import { cn } from '@/lib/utils';

import { useHLSPlayer } from '../contexts/hls-player-provider';
import type { IControlsProps } from '../types';

type MoreOptionsProps = IControlsProps;

function MoreOptions({ className }: MoreOptionsProps) {
	const { uiState, toggleFullscreen, onDownloadVideoClick } = useHLSPlayer();
	const { isFullscreen } = uiState;
	const [isDownloading, setIsDownloading] = useState(false);

	const handleDownloadVideoClick = useCallback(
		async (e: React.MouseEvent<Element>) => {
			e.preventDefault();

			setIsDownloading(true);
			await onDownloadVideoClick?.();
			setIsDownloading(false);
		},
		[onDownloadVideoClick]
	);

	const dropdownOptions = useMemo<DropdownOption[]>(() => {
		const options: DropdownOption[] = [];

		// Fullscreen control
		options.push({
			label: isFullscreen ? 'Exit Fullscreen' : 'Fullscreen',
			value: 'fullscreen',
			icon: isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />,
			onClick: toggleFullscreen,
		});

		// Download option
		if (onDownloadVideoClick) {
			options.push({
				label: 'Download',
				value: 'download',
				icon: isDownloading ? <Loader2 className="size-4 animate-spin" /> : <Download size={16} />,
				onClick: handleDownloadVideoClick,
				disabled: isDownloading,
			});
		}

		return options;
	}, [
		isFullscreen,
		isDownloading,
		toggleFullscreen,
		onDownloadVideoClick,
		handleDownloadVideoClick,
	]);

	return (
		<SimpleDropdown
			options={dropdownOptions}
			dropdownAlign="end"
			className="min-w-[150px]"
		>
			<button
				className={cn(
					'flex h-7 w-6 items-center justify-center rounded-md text-white transition-colors hover:bg-white/20',
					className
				)}
				aria-label="More options"
			>
				<MoreVertical size={18} />
			</button>
		</SimpleDropdown>
	);
}

export default React.memo(MoreOptions);
